// ReduxToolkit/Reducers/PanelConfigReducer.ts
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { get } from '../../hook/request';

type Theme = {
  primary: string;
  secondary: string;
};

export interface AppConfig {
  title: string;
  nome: string;
  theme: Theme;
  images: {
    logo: string;
    favicon: string;
  };
}

interface PanelDataFromApi {
  ID: number;
  nome: string;
  colore_primario: string;
  colore_secondario: string;
  logo: string;
  favicon: string;
  [key: string]: any;
}

interface PanelDataResponse {
  status: string;
  data: PanelDataFromApi;
}

interface PanelConfigState {
  data: AppConfig | null;
  loading: boolean;
  error: string | null;
}

const STORAGE_KEY = 'panelConfig';

// --- helper per leggere da localStorage ---
function loadFromStorage(): AppConfig | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AppConfig;
  } catch {
    return null;
  }
}

// --- stato iniziale: prova a leggere dalla cache ---
const cachedConfig = loadFromStorage();

const initialState: PanelConfigState = {
  data: cachedConfig,
  loading: !cachedConfig, // se ho cache, non sono "veramente" in loading
  error: null,
};


export const fetchPanelConfig = createAsyncThunk<AppConfig>(
  'panelConfig/fetch',
  async () => {

    const res = await fetch('/assets/pannel/config.json?cache=' + Date.now());
    if (!res.ok) {
      throw new Error("Impossibile leggere config.json");
    }

    const data = await res.json();

    // mappo nel tipo AppConfig
    const mapped: AppConfig = {
      title: data.title,
      nome: data.nome,
      theme: {
        primary: data.theme.primary,
        secondary: data.theme.secondary,
      },
      images: {
        logo: data.images.logo,
        favicon: data.images.favicon ?? '',  
      },
    };

    return mapped;
  }
);



const panelConfigSlice = createSlice({
  name: 'panelConfig',
  initialState,
  reducers: {
    setPanelConfig(state, action: PayloadAction<AppConfig>) {
      state.data = action.payload;
      state.error = null;
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(action.payload));
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPanelConfig.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPanelConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;

        // aggiorna cache
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(action.payload));
        }
      })
      .addCase(fetchPanelConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Errore nel caricamento configurazione';
      });
  },
});

export const { setPanelConfig } = panelConfigSlice.actions;
export default panelConfigSlice.reducer;
